/**
 * @feature quote-requests
 * @layer presentation/components
 *
 * DescriptionSection - 전달사항
 * Displays description with rich text HTML rendering
 */

import { type FC } from "react";
import { MessageSquare } from "@semo/icons";
import DOMPurify from "dompurify";
import { cn } from "../../../utils/cn";
import { restrictRichTextStyles } from "../../../utils/restrictRichTextStyles";
import type { QuoteReviewSectionProps } from "../types";

/**
 * Description Section Component
 * Shows description/message with rich text HTML rendering
 */
export const DescriptionSection: FC<
  QuoteReviewSectionProps & { children?: React.ReactNode; title?: string }
> = ({ data, onEdit, children }) => {
  // Extract description
  const rawDescription =
    typeof data.description === "string" ? data.description : undefined;

  // Sanitize HTML - remove extra whitespace and newlines
  const description = rawDescription ? rawDescription.trim() : "";

  // DOMPurify로 HTML 새니타이제이션 (XSS 방지) → 인라인 스타일·클래스 허용 목록(GS A2-21)
  const sanitizedHtml = restrictRichTextStyles(
    DOMPurify.sanitize(description, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "em",
        "u",
        "s",
        "ul",
        "ol",
        "li",
        "h1",
        "h2",
        "h3",
        "a",
        "span",
        "blockquote",
      ],
      ALLOWED_ATTR: ["href", "style", "class", "target", "rel"],
    }),
  );

  // Check if there is actual text content (not just empty HTML tags like <p></p>)
  const textContent = sanitizedHtml.replace(/<[^>]*>/g, "").trim();
  const isEmpty = !textContent;

  return (
    <div className="bg-white rounded-[20px] p-6 sm:p-8 relative overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 relative z-10">
        <div className="flex items-center gap-3">
          {/* Icon */}
          <div className="w-10 h-10 rounded-[12px] bg-primary/5 flex items-center justify-center text-primary">
            <MessageSquare className="w-5 h-5" />
          </div>
          {/* Title */}
          <div className="flex items-center gap-2.5">
            <h3 className="text-lg font-semibold text-gray-900">전달사항</h3>
            {isEmpty && (
              <span className="text-sm text-gray-400 font-medium">
                없음
              </span>
            )}
          </div>
        </div>

        {/* Edit button - optional */}
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className={cn(
              "px-4 py-1.5",
              "text-gray-400",
              "rounded-[10px]",
              "hover:bg-gray-50 hover:text-primary",
              "transition-all",
              "text-sm font-semibold",
            )}
          >
            수정
          </button>
        )}
      </div>

      {/* Content - Rich Text (Show only if not empty) */}
      {!isEmpty && (
        <div className="relative z-10 bg-gray-50/50 p-5 sm:p-6 rounded-[16px] max-h-[400px] overflow-y-auto">
          <div
            className="text-base sm:text-base leading-relaxed text-gray-700 whitespace-pre-wrap"
            style={
              {
                "--tw-prose-body": "rgb(55 65 81)",
                "--tw-prose-invert-body": "rgb(209 213 219)",
              } as any
            }
          >
            <style>{`
              .description-html p {
                margin-bottom: 1rem;
              }
              .description-html p:last-child {
                margin-bottom: 0;
              }
              .description-html strong {
                font-weight: 700;
              }
              .description-html em {
                font-style: italic;
              }
              .description-html a {
                color: var(--color-primary, #2e7fee);
                text-decoration: underline;
              }
              .description-html ul,
              .description-html ol {
                margin-left: 1.5rem;
                margin-bottom: 1rem;
              }
              .description-html li {
                margin-bottom: 0.5rem;
              }
              .description-html h1,
              .description-html h2,
              .description-html h3 {
                font-weight: 700;
                margin-bottom: 0.5rem;
              }
              .description-html h1 {
                font-size: 1.5rem;
              }
              .description-html h2 {
                font-size: 1.25rem;
              }
              .description-html h3 {
                font-size: 1.1rem;
              }
              .description-html blockquote {
                border-left: 4px solid var(--color-primary, #2e7fee);
                padding-left: 1rem;
                margin-bottom: 1rem;
                font-style: italic;
                background: rgba(46, 127, 238, 0.05);
                padding: 1rem;
                border-radius: 0 8px 8px 0;
              }
            `}</style>
            <div
              className="description-html"
              dangerouslySetInnerHTML={{
                __html: sanitizedHtml,
              }}
            />
          </div>
        </div>
      )}

      {/* Children & Additional Content */}
      {children}
    </div>
  );
};
